import React, { useState, useRef, useEffect } from 'react';
import {
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  TouchableOpacity,
  Modal,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import styled from 'styled-components/native';
import { useNavigation } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { AppBar, Text, Card } from '@ui';
import { useTheme } from '@theme/ThemeProvider';

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const ChatContainer = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const MessagesList = styled.ScrollView`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const MessageRow = styled.View<{ isUser: boolean }>`
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
  ${({ isUser }) => (isUser ? 'flex-direction: row-reverse;' : '')}
`;

const Avatar = styled.Image`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  ${({ isUser }: { isUser: boolean }) => (isUser ? 'margin-left: 0px;' : 'margin-right: 8px;')}
`;

const MessageBubble = styled.View<{ isUser: boolean }>`
  max-width: 75%;
  padding: ${({ theme }) => theme.spacing.base}px ${({ theme }) => theme.spacing.md}px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  background-color: ${({ theme, isUser }) =>
    isUser ? theme.colors.brandPrimary : theme.colors.neutral200 || '#F0F0F0'};
`;

const MessageText = styled(Text)<{ isUser: boolean }>`
  color: ${({ isUser, theme }) => (isUser ? theme.colors.textOnPrimary : theme.colors.textPrimary)};
`;

const MessageContent = styled.View<{ isUser?: boolean }>`
  max-width: 75%;
  ${({ isUser }) => (isUser ? 'margin-left: -8px;' : '')}
`;

const ActionCard = styled(Card)<{ isUser?: boolean; isAction?: boolean }>`
  margin-top: ${({ theme }) => theme.spacing.sm}px;
  padding: ${({ theme }) => theme.spacing.base}px;
  background-color: ${({ theme, isUser, isAction }) => {
    if (isUser) return theme.colors.brandPrimary;
    if (isAction) return theme.colors.brandPrimary;
    return theme.colors.surface;
  }};
  border-radius: ${({ theme }) => theme.radius.md}px;
  flex-direction: row;
  align-items: center;
  shadow-color: ${({ theme }) => theme.colors.textPrimary};
  shadow-offset: 0px 2px;
  shadow-opacity: 0.1;
  shadow-radius: 4px;
  elevation: 2;
`;

const ActionCardIcon = styled.View<{ isUser?: boolean; isAction?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  background-color: ${({ theme, isUser, isAction }) => {
    if (isUser || isAction) return 'rgba(255, 255, 255, 0.2)';
    return theme.colors.neutral100;
  }};
  align-items: center;
  justify-content: center;
  margin-right: ${({ theme }) => theme.spacing.sm}px;
`;

const ActionCardText = styled(Text)<{ isUser?: boolean; isAction?: boolean }>`
  flex: 1;
  color: ${({ theme, isUser, isAction }) => {
    if (isUser || isAction) return theme.colors.textOnPrimary;
    return theme.colors.textPrimary;
  }};
  font-weight: 500;
`;

const InputContainer = styled.View`
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base}px;
  background-color: ${({ theme }) => theme.colors.surface};
  border-top-width: 1px;
  border-top-color: ${({ theme }) => theme.colors.border};
`;

const Input = styled.TextInput`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.sm}px ${({ theme }) => theme.spacing.base}px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  border-width: 1px;
  border-color: ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.body.fontSize}px;
  margin-right: ${({ theme }) => theme.spacing.sm}px;
`;

const SendButton = styled.TouchableOpacity`
  width: 44px;
  height: 44px;
  border-radius: 22px;
  background-color: ${({ theme }) => theme.colors.brandPrimary};
  align-items: center;
  justify-content: center;
`;

const QuickActionsContainer = styled.View`
  padding: ${({ theme }) => theme.spacing.base}px;
  border-top-width: 1px;
  border-top-color: ${({ theme }) => theme.colors.border};
  background-color: ${({ theme }) => theme.colors.surface};
`;

const QuickActionsTitle = styled(Text)`
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const QuickActionsRow = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
`;

const QuickActionButton = styled.TouchableOpacity`
  padding: ${({ theme }) => theme.spacing.sm}px ${({ theme }) => theme.spacing.base}px;
  border-radius: ${({ theme }) => theme.radius.md}px;
  border-width: 1px;
  border-color: ${({ theme }) => theme.colors.brandPrimary};
  background-color: ${({ theme }) => theme.colors.surface};
  margin-right: ${({ theme }) => theme.spacing.sm}px;
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
`;

const QuickActionText = styled(Text)`
  color: ${({ theme }) => theme.colors.brandPrimary};
  font-size: ${({ theme }) => theme.typography.bodySmall.fontSize}px;
`;

const ModalOverlay = styled(SafeAreaView)`
  flex: 1;
  background-color: rgba(0, 0, 0, 0.5);
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 500px;
  max-height: 80%;
  padding: ${({ theme }) => theme.spacing.xl}px;
`;

const ModalHeader = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const ModalTitle = styled(Text)`
  font-size: ${({ theme }) => theme.typography.headline.fontSize}px;
  font-weight: bold;
`;

const CloseButton = styled(TouchableOpacity)`
  width: 32px;
  height: 32px;
  border-radius: 16px;
  background-color: ${({ theme }) => theme.colors.neutral200};
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled(ScrollView)`
  flex: 1;
`;

const InfoRow = styled.View`
  flex-direction: row;
  padding: ${({ theme }) => theme.spacing.base}px 0;
  border-bottom-width: 1px;
  border-bottom-color: ${({ theme }) => theme.colors.border};
`;

const InfoLabel = styled(Text)`
  font-weight: 600;
  width: 120px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const InfoValue = styled(Text)`
  flex: 1;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

interface MessageAction {
  type: 'benefit-summary' | 'find-doctor' | 'product-list';
  title: string;
  icon: string;
  isAction?: boolean; // If true, use AIA red background for bot messages
}

interface QuickQuestion {
  id: string;
  text: string;
  responseText: string;
  responseAction?: MessageAction;
}

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
  action?: MessageAction;
}

const ChatbotScreen: React.FC = () => {
  const theme = useTheme();
  const navigation = useNavigation();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hi, how can I assist you today?',
      isUser: false,
      timestamp: new Date(),
    },
    {
      id: '2',
      text: 'Does my group medical cover my baby? What are the benefits?',
      isUser: true,
      timestamp: new Date(),
    },
    {
      id: '3',
      text: 'Is the coverage sufficient? Do I need to arrange additional coverage?',
      isUser: true,
      timestamp: new Date(),
    },
    {
      id: '4',
      text: 'Yes! Your group medical coverage covers dependent and your baby will covered.',
      isUser: false,
      timestamp: new Date(),
    },
    {
      id: '5',
      text: 'Please find your benefit summary below and the product list for your reference. Good News! You are also entitled to additional CS offer.',
      isUser: false,
      timestamp: new Date(),
      action: {
        type: 'product-list',
        title: 'Product list - Mom-to-be',
        icon: 'list',
      },
    },
    {
      id: '6',
      text: 'I would like to check my maternity coverage.',
      isUser: true,
      timestamp: new Date(),
    },
    {
      id: '7',
      text: 'Your group medical insurance plan provides the following maternity coverage under the Basic Plan A.',
      isUser: false,
      timestamp: new Date(),
      action: {
        type: 'benefit-summary',
        title: 'Benefit Summary – HMO-NORMAL MATERNITY',
        icon: 'description',
      },
    },
    {
      id: '8',
      text: 'I am not sure where to see a local doctor. Where can I find a doctor?',
      isUser: true,
      timestamp: new Date(),
    },
    {
      id: '9',
      text: 'Step: 1. Tap "Login" on the app',
      isUser: false,
      timestamp: new Date(),
      action: {
        type: 'find-doctor',
        title: 'Find doctor/hospitals near you',
        icon: 'medical-services',
      },
    },
  ]);
  const [inputText, setInputText] = useState('');
  const scrollViewRef = useRef<ScrollView>(null);
  const [isProductListModalVisible, setIsProductListModalVisible] = useState(false);
  const [isBenefitSummaryModalVisible, setIsBenefitSummaryModalVisible] = useState(false);
  const [isFindDoctorModalVisible, setIsFindDoctorModalVisible] = useState(false);

  // Quick questions that users can click
  const quickQuestions: QuickQuestion[] = [
    {
      id: 'q1',
      text: 'Does my group medical cover my baby? What are the benefits?',
      responseText: 'Yes! Your group medical coverage covers dependent and your baby will covered.',
      responseAction: {
        type: 'product-list',
        title: 'Product list - Mom-to-be',
        icon: 'list',
      },
    },
    {
      id: 'q2',
      text: 'Is the coverage sufficient? Do I need to arrange additional coverage?',
      responseText:
        'Please find your benefit summary below and the product list for your reference. Good News! You are also entitled to additional CS offer.',
      responseAction: {
        type: 'product-list',
        title: 'Product list - Mom-to-be',
        icon: 'list',
      },
    },
    {
      id: 'q3',
      text: 'I would like to check my maternity coverage.',
      responseText:
        'Your group medical insurance plan provides the following maternity coverage under the Basic Plan A.',
      responseAction: {
        type: 'benefit-summary',
        title: 'Benefit Summary – HMO-NORMAL MATERNITY',
        icon: 'description',
      },
    },
    {
      id: 'q4',
      text: 'Where can I find a doctor?',
      responseText: 'Step: 1. Tap "Login" on the app',
      responseAction: {
        type: 'find-doctor',
        title: 'Find doctor/hospitals near you',
        icon: 'medical-services',
      },
    },
  ];

  useEffect(() => {
    // Auto-scroll to bottom when new messages are added
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [messages]);

  const handleSend = () => {
    if (inputText.trim()) {
      const newMessage: Message = {
        id: Date.now().toString(),
        text: inputText.trim(),
        isUser: true,
        timestamp: new Date(),
      };

      setMessages([...messages, newMessage]);
      setInputText('');

      // Simulate bot response
      setTimeout(() => {
        const botResponse: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Thank you for your message. How can I assist you further?',
          isUser: false,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, botResponse]);
      }, 1000);
    }
  };

  const handleActionPress = (action: MessageAction) => {
    if (action.type === 'find-doctor') {
      setIsFindDoctorModalVisible(true);
    } else if (action.type === 'benefit-summary') {
      setIsBenefitSummaryModalVisible(true);
    } else if (action.type === 'product-list') {
      setIsProductListModalVisible(true);
    }
  };

  const handleQuickQuestion = (question: QuickQuestion) => {
    // Add user question as a message
    const userMessage: Message = {
      id: Date.now().toString(),
      text: question.text,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);

    // Add bot response after a short delay
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: question.responseText,
        isUser: false,
        timestamp: new Date(),
        action: question.responseAction,
      };
      setMessages(prev => [...prev, botResponse]);
    }, 800);
  };

  const handleMenuPress = () => {
    // Handle menu actions (clear chat, settings, etc.)
  };

  return (
    <Container>
      <AppBar
        title="Chatbot"
        leftIcon="arrow-back"
        onLeftPress={() => navigation.goBack()}
        rightIcon="more-vert"
        onRightPress={handleMenuPress}
      />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : Platform.OS === 'web' ? undefined : 'height'}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : Platform.OS === 'web' ? 0 : 0}
      >
        <ChatContainer>
          <MessagesList
            ref={scrollViewRef}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingBottom: 16 }}
          >
            {messages.map(message => (
              <MessageRow key={message.id} isUser={message.isUser}>
                <Avatar
                  source={
                    message.isUser
                      ? // eslint-disable-next-line global-require
                        require('../../assets/chatbot-user.png')
                      : // eslint-disable-next-line global-require
                        require('../../assets/chatbot-bot.png')
                  }
                  isUser={message.isUser}
                />
                <MessageContent isUser={message.isUser}>
                  <MessageBubble isUser={message.isUser}>
                    <MessageText variant="body" isUser={message.isUser}>
                      {message.text}
                    </MessageText>
                  </MessageBubble>
                  {message.action && (
                    <TouchableOpacity
                      onPress={() => handleActionPress(message.action!)}
                      activeOpacity={0.7}
                    >
                      <ActionCard
                        isUser={message.isUser}
                        isAction={message.action.isAction}
                        padding="none"
                      >
                        <ActionCardIcon isUser={message.isUser || message.action.isAction}>
                          <Icon
                            name={message.action.icon}
                            size={20}
                            color={
                              message.isUser || message.action.isAction
                                ? theme.colors.textOnPrimary
                                : theme.colors.textPrimary
                            }
                          />
                        </ActionCardIcon>
                        <ActionCardText
                          variant="bodyMedium"
                          isUser={message.isUser}
                          isAction={message.action.isAction}
                        >
                          {message.action.title}
                        </ActionCardText>
                      </ActionCard>
                    </TouchableOpacity>
                  )}
                </MessageContent>
              </MessageRow>
            ))}
          </MessagesList>

          <QuickActionsContainer>
            <QuickActionsTitle variant="caption" color="textSecondary">
              Quick questions:
            </QuickActionsTitle>
            <QuickActionsRow>
              {quickQuestions.map(question => (
                <QuickActionButton
                  key={question.id}
                  onPress={() => handleQuickQuestion(question)}
                  activeOpacity={0.7}
                >
                  <QuickActionText variant="bodySmall">{question.text}</QuickActionText>
                </QuickActionButton>
              ))}
            </QuickActionsRow>
          </QuickActionsContainer>

          <InputContainer>
            <Input
              placeholder="Type your message..."
              placeholderTextColor={theme.colors.textTertiary}
              value={inputText}
              onChangeText={setInputText}
              multiline
              maxLength={500}
              onSubmitEditing={handleSend}
            />
            <SendButton onPress={handleSend} disabled={!inputText.trim()}>
              <Icon name="send" size={20} color={theme.colors.textOnPrimary} />
            </SendButton>
          </InputContainer>
        </ChatContainer>
      </KeyboardAvoidingView>

      {/* Product List Modal */}
      <Modal
        visible={isProductListModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsProductListModalVisible(false)}
      >
        <ModalOverlay edges={['top', 'bottom']}>
          <ModalContent padding="xl">
            <ModalHeader>
              <ModalTitle variant="headline" color="textPrimary">
                Product list - Mom-to-be
              </ModalTitle>
              <CloseButton onPress={() => setIsProductListModalVisible(false)}>
                <Icon name="close" size={20} color={theme.colors.textPrimary} />
              </CloseButton>
            </ModalHeader>
            <ModalBody showsVerticalScrollIndicator={false}>
              <Text
                variant="titleMedium"
                color="textPrimary"
                style={{ marginBottom: 20, fontWeight: '700' }}
              >
                Available Products for Expectant Mothers
              </Text>

              {/* Product 1 */}
              <View
                style={{
                  marginBottom: 24,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Product 1: AIA Health Mommy - Premium Plan
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Product Code</InfoLabel>
                  <InfoValue variant="bodyMedium">AIA-HM-PRE-2024</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Annual Premium</InfoLabel>
                  <InfoValue
                    variant="bodyMedium"
                    style={{ fontWeight: '600', color: theme.colors.brandPrimary }}
                  >
                    HK$ 12,000 - 18,000 (depends on age & coverage)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Payment Mode</InfoLabel>
                  <InfoValue variant="bodyMedium">Monthly, Quarterly, or Annual</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Waiting Period</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    10 months (coverage starts after 10 months from enrollment)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Coverage Scope</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Comprehensive maternity coverage including prenatal, delivery, and postnatal
                    care
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Prenatal Checkups</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Up to 10 consultations at network clinics (100% coverage, no co-payment)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Ultrasound Scans</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Up to 4 scans (2D, 3D, 4D available at designated centers)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Blood Tests</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Complete blood count, glucose tolerance test, genetic screening covered
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Delivery Coverage</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Normal delivery: HK$ 35,000 | C-section: HK$ 50,000
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Hospital Room</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Semi-private room (2-bed) for 3-5 days (normal) / 5-7 days (C-section)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Postnatal Care</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    6 weeks postnatal checkups + 2 home visits by registered midwife
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Newborn Coverage</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    First 30 days: Full coverage including vaccinations (BCG, Hepatitis B)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Network Coverage</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    200+ clinics, 30+ hospitals across Hong Kong, Macau, and Mainland China
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Cash Supplement</InfoLabel>
                  <InfoValue
                    variant="bodyMedium"
                    style={{ fontWeight: '600', color: theme.colors.brandPrimary }}
                  >
                    Special Offer: HK$ 5,000 - 8,000 upon successful enrollment (limited time)
                  </InfoValue>
                </InfoRow>
              </View>

              {/* Product 2 */}
              <View
                style={{
                  marginBottom: 24,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Product 2: AIA Health Mommy - Basic Plan
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Product Code</InfoLabel>
                  <InfoValue variant="bodyMedium">AIA-HM-BAS-2024</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Annual Premium</InfoLabel>
                  <InfoValue variant="bodyMedium">HK$ 8,000 - 12,000</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Prenatal Checkups</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Up to 8 consultations at network clinics
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Ultrasound Scans</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    3 scans (2D only at designated centers)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Delivery Coverage</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Normal: HK$ 25,000 | C-section: HK$ 35,000
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Hospital Room</InfoLabel>
                  <InfoValue variant="bodyMedium">Standard ward (4-bed) for 2-3 days</InfoValue>
                </InfoRow>
              </View>

              {/* Benefits Comparison */}
              <View
                style={{ padding: 12, backgroundColor: theme.colors.neutral100, borderRadius: 8 }}
              >
                <Text variant="bodySmall" color="textSecondary" style={{ lineHeight: 20 }}>
                  💡 <Text style={{ fontWeight: '600' }}>Tip:</Text> Both plans include 24/7
                  maternity hotline support, nutrition counseling, and access to AIA Vitality
                  wellness program. Premium plan members enjoy priority booking at network
                  hospitals.
                </Text>
              </View>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>

      {/* Benefit Summary Modal */}
      <Modal
        visible={isBenefitSummaryModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsBenefitSummaryModalVisible(false)}
      >
        <ModalOverlay edges={['top', 'bottom']}>
          <ModalContent padding="xl">
            <ModalHeader>
              <ModalTitle variant="headline" color="textPrimary">
                Benefit Summary – HMO-NORMAL MATERNITY
              </ModalTitle>
              <CloseButton onPress={() => setIsBenefitSummaryModalVisible(false)}>
                <Icon name="close" size={20} color={theme.colors.textPrimary} />
              </CloseButton>
            </ModalHeader>
            <ModalBody showsVerticalScrollIndicator={false}>
              <Text
                variant="titleMedium"
                color="textPrimary"
                style={{ marginBottom: 20, fontWeight: '700' }}
              >
                Your Current Coverage Details
              </Text>

              {/* Policy Information */}
              <View
                style={{
                  marginBottom: 20,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Policy Information
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Policy Number</InfoLabel>
                  <InfoValue variant="bodyMedium" style={{ fontWeight: '600' }}>
                    AIA-GRP-2024-8456
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Insured Name</InfoLabel>
                  <InfoValue variant="bodyMedium">Member Name (Employee)</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Plan Type</InfoLabel>
                  <InfoValue variant="bodyMedium">Basic Plan A (Group Medical Insurance)</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Coverage Period</InfoLabel>
                  <InfoValue variant="bodyMedium">January 1, 2024 - December 31, 2024</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Renewal Date</InfoLabel>
                  <InfoValue variant="bodyMedium">January 1, 2025 (auto-renewal)</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Status</InfoLabel>
                  <InfoValue
                    variant="bodyMedium"
                    style={{ color: theme.colors.success || '#4CAF50', fontWeight: '600' }}
                  >
                    Active ✓
                  </InfoValue>
                </InfoRow>
              </View>

              {/* Maternity Coverage Details */}
              <View
                style={{
                  marginBottom: 20,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Maternity Coverage Details
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Coverage Type</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Full coverage for normal maternity within HMO network
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Network Providers</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    HMO Network: 200+ clinics, 30+ hospitals across Hong Kong
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Geographic Coverage</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Hong Kong, Macau, and selected cities in Mainland China
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Annual Limit</InfoLabel>
                  <InfoValue
                    variant="bodyMedium"
                    style={{ fontWeight: '600', color: theme.colors.brandPrimary }}
                  >
                    HK$ 50,000 per pregnancy (renewable annually)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Lifetime Limit</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    No lifetime limit (subject to annual limit)
                  </InfoValue>
                </InfoRow>
              </View>

              {/* Prenatal Care Benefits */}
              <View
                style={{
                  marginBottom: 20,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Prenatal Care Benefits
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Consultations</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Up to 8 consultations at network clinics (100% covered, no co-payment)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Ultrasound Scans</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    3 scans per pregnancy (6-8 weeks, 12 weeks, 20 weeks anomaly scan)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Blood Tests</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Complete blood count, glucose tolerance test (GTT), genetic screening
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Urine Tests</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Routine urinalysis at each prenatal visit
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Vitamin Supplements</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Prenatal vitamins (folic acid, iron) up to HK$ 500 per pregnancy
                  </InfoValue>
                </InfoRow>
              </View>

              {/* Delivery & Hospital Benefits */}
              <View
                style={{
                  marginBottom: 20,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Delivery & Hospital Benefits
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Normal Delivery</InfoLabel>
                  <InfoValue variant="bodyMedium" style={{ fontWeight: '600' }}>
                    HK$ 25,000 (within network)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">C-Section Delivery</InfoLabel>
                  <InfoValue variant="bodyMedium" style={{ fontWeight: '600' }}>
                    HK$ 35,000 (within network)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Hospital Room</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Standard ward (4-bed) or semi-private room (2-bed) charges
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Hospital Stay</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Up to 3 days (normal delivery) / 5 days (C-section delivery)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Anesthesia</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Epidural anesthesia covered (if medically necessary)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Operating Theater</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Operating room charges included for C-section
                  </InfoValue>
                </InfoRow>
              </View>

              {/* Postnatal & Newborn Benefits */}
              <View
                style={{
                  marginBottom: 20,
                  paddingBottom: 16,
                  borderBottomWidth: 1,
                  borderBottomColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Postnatal & Newborn Benefits
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Postnatal Checkups</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    2 postnatal consultations within 6 weeks after delivery
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Newborn Coverage</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Baby covered as dependent from birth (first 30 days free)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Newborn Vaccinations</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    BCG, Hepatitis B (first dose) covered within first 30 days
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Circumcision</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Covered for male newborns (if medically necessary)
                  </InfoValue>
                </InfoRow>
              </View>

              {/* Claim Information */}
              <View style={{ marginBottom: 16 }}>
                <Text
                  variant="bodyMedium"
                  color="brandPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Claim Process & Contact
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">Billing Method</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Direct billing at network providers (no upfront payment required)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Reimbursement</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Cash reimbursement within 14 working days (for non-network providers)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Required Documents</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Medical receipts, doctor's certificate, delivery report
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Claim Hotline</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    2881 3333 (Mon-Fri 9am-6pm, Sat 9am-1pm)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">Online Claims</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Available via AIA Member Portal or Mobile App
                  </InfoValue>
                </InfoRow>
              </View>

              {/* Important Notes */}
              <View
                style={{ padding: 12, backgroundColor: theme.colors.neutral100, borderRadius: 8 }}
              >
                <Text
                  variant="bodySmall"
                  color="textSecondary"
                  style={{ lineHeight: 20, marginBottom: 8, fontWeight: '600' }}
                >
                  ⚠️ Important Notes:
                </Text>
                <Text variant="bodySmall" color="textSecondary" style={{ lineHeight: 20 }}>
                  • Pre-authorization required for elective C-section (apply 2 weeks in advance)
                  {'\n'}• Coverage applies to services within network providers only{'\n'}• Annual
                  limit resets on policy renewal date{'\n'}• Waiting period: 10 months from policy
                  enrollment date{'\n'}• Exclusions: Cosmetic procedures, experimental treatments,
                  pre-existing conditions
                </Text>
              </View>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>

      {/* Find Doctor Modal */}
      <Modal
        visible={isFindDoctorModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsFindDoctorModalVisible(false)}
      >
        <ModalOverlay edges={['top', 'bottom']}>
          <ModalContent padding="xl">
            <ModalHeader>
              <ModalTitle variant="headline" color="textPrimary">
                Find doctor/hospitals near you
              </ModalTitle>
              <CloseButton onPress={() => setIsFindDoctorModalVisible(false)}>
                <Icon name="close" size={20} color={theme.colors.textPrimary} />
              </CloseButton>
            </ModalHeader>
            <ModalBody showsVerticalScrollIndicator={false}>
              <Text
                variant="bodyMedium"
                color="textPrimary"
                style={{ marginBottom: 16, fontWeight: '600' }}
              >
                How to Find Network Doctors & Hospitals:
              </Text>

              <InfoRow>
                <InfoLabel variant="bodyMedium">Method 1</InfoLabel>
                <InfoValue variant="bodyMedium">
                  Via AIA Member Companion App: Login → Journeys → Care & Clinics
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel variant="bodyMedium">Method 2</InfoLabel>
                <InfoValue variant="bodyMedium">
                  Call AIA Hotline: 2881 3333 (Mon-Fri 9am-6pm)
                </InfoValue>
              </InfoRow>
              <InfoRow>
                <InfoLabel variant="bodyMedium">Method 3</InfoLabel>
                <InfoValue variant="bodyMedium">
                  Visit www.aia.com.hk → Find a Provider → Search by district
                </InfoValue>
              </InfoRow>

              <View
                style={{
                  marginTop: 24,
                  paddingTop: 16,
                  borderTopWidth: 1,
                  borderTopColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="textPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Recommended Maternity Hospitals in Hong Kong:
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">🏥 Hospital</InfoLabel>
                  <InfoValue variant="bodyMedium">Queen Mary Hospital (Pok Fu Lam)</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📍 Location</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    102 Pokfulam Road, Pok Fu Lam, Hong Kong Island
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📞 Contact</InfoLabel>
                  <InfoValue variant="bodyMedium">Tel: 2255 3838 | Emergency: 2255 3000</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">⏰ Maternity Ward</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    24/7 Service | Booking required for deliveries
                  </InfoValue>
                </InfoRow>

                <View style={{ height: 16 }} />

                <InfoRow>
                  <InfoLabel variant="bodyMedium">🏥 Hospital</InfoLabel>
                  <InfoValue variant="bodyMedium">Prince of Wales Hospital (Sha Tin)</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📍 Location</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    30-32 Ngan Shing Street, Sha Tin, New Territories
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📞 Contact</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Tel: 2632 2211 | Maternity Ward: 2632 2840
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">⏰ Services</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Comprehensive maternity services | Level 6 Maternity Unit
                  </InfoValue>
                </InfoRow>

                <View style={{ height: 16 }} />

                <InfoRow>
                  <InfoLabel variant="bodyMedium">🏥 Hospital</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Gleneagles Hospital Hong Kong (Wong Chuk Hang)
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📍 Location</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    1 Nam Fung Path, Wong Chuk Hang, Hong Kong Island
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📞 Contact</InfoLabel>
                  <InfoValue variant="bodyMedium">Tel: 3153 9000 | Obstetrics: 3153 9033</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">⭐ Features</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Private hospital | Luxury suites | International standard care
                  </InfoValue>
                </InfoRow>
              </View>

              <View
                style={{
                  marginTop: 24,
                  paddingTop: 16,
                  borderTopWidth: 1,
                  borderTopColor: theme.colors.border,
                }}
              >
                <Text
                  variant="bodyMedium"
                  color="textPrimary"
                  style={{ marginBottom: 12, fontWeight: '600' }}
                >
                  Recommended Obstetricians in Network:
                </Text>

                <InfoRow>
                  <InfoLabel variant="bodyMedium">👨‍⚕️ Dr. Wong</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Dr. Wong Chi Keung - Central Obstetrics Clinic
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📍 Address</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Room 1201, Central Building, 1-3 Pedder Street, Central
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📞 Phone</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Tel: 2522 1234 | Languages: Cantonese, English, Mandarin
                  </InfoValue>
                </InfoRow>

                <View style={{ height: 12 }} />

                <InfoRow>
                  <InfoLabel variant="bodyMedium">👩‍⚕️ Dr. Chan</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Dr. Chan Mei Ling - Tsim Sha Tsui Women's Clinic
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📍 Address</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Unit 1505, Harbour City Office Tower, Tsim Sha Tsui
                  </InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel variant="bodyMedium">📞 Phone</InfoLabel>
                  <InfoValue variant="bodyMedium">
                    Tel: 2735 6789 | Specializes in high-risk pregnancies
                  </InfoValue>
                </InfoRow>
              </View>

              <View
                style={{
                  marginTop: 24,
                  padding: 12,
                  backgroundColor: theme.colors.neutral100,
                  borderRadius: 8,
                }}
              >
                <Text variant="bodySmall" color="textSecondary" style={{ lineHeight: 18 }}>
                  💡 Tip: Always call to confirm appointment availability. For emergency situations,
                  go directly to the Accident & Emergency (A&E) department.
                </Text>
              </View>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      </Modal>
    </Container>
  );
};

export default ChatbotScreen;
